import { createSelector, MemoizedSelector } from '@ngrx/store';
import { MeasurementTools } from '../../types/measurement-tools';
import {
    MeasuringEquipments,
    MeasuringEquipmentsControlName,
    MeasuringEquipmentTableRow,
} from '../../types/measuring-equipments';
import { FormsState } from '../state/pace-forms.state';
import { getMeasuringEquipments } from './form.selector';
import { getPaceFormsState } from './pace-forms.selector';

function getColumnValue(row: MeasuringEquipmentTableRow | undefined, columnName: string): string {
    return row?.columns.find((column) => column.columnName === columnName)?.value ?? '';
}

export const getMeasurementToolsState = createSelector(getPaceFormsState, (state) => state.measurementTools);

export const getMeasurementTools = createSelector(getMeasurementToolsState, (state) => state.tools);

export const getMeasurementToolsStatus = createSelector(getMeasurementToolsState, (state) => state.toolsStatus);

export const getFilteredMeasurementTools = (props: {
    rowId: string;
}): MemoizedSelector<
    FormsState,
    MeasurementTools[],
    (tools: MeasurementTools[], measuringEquipments: MeasuringEquipments | undefined) => MeasurementTools[]
> =>
    createSelector(getMeasurementTools, getMeasuringEquipments, (tools, measuringEquipments) => {
        const rows = measuringEquipments?.rows ?? [];
        const currentRow = rows.find((row) => row.rowId === props.rowId);
        const description = getColumnValue(currentRow, MeasuringEquipmentsControlName.Description);
        const equipmentId = getColumnValue(currentRow, MeasuringEquipmentsControlName.EquipmentId);

        const equipmentIdsUsedByOtherRows = new Set(
            rows
                .filter((row) => row.rowId !== props.rowId)
                .map((row) => getColumnValue(row, MeasuringEquipmentsControlName.EquipmentId)),
        );

        const availableTools = tools.filter((tool) => !equipmentIdsUsedByOtherRows.has(tool.equipmentId));

        const descriptionMatch =
            description !== '' ? availableTools.find((tool) => tool.description === description) : undefined;
        const equipmentIdMatch =
            equipmentId !== '' ? availableTools.find((tool) => tool.equipmentId === equipmentId) : undefined;

        // Both fields resolving to the same tool means it's a committed selection: show the full list again.
        if (descriptionMatch && descriptionMatch === equipmentIdMatch) {
            return availableTools;
        }

        // A field that exactly matches a tool the other field does NOT (including the other field
        // being empty) is either a stale leftover from a previous selection or a lone exact match with
        // nothing else to search by — either way, suppress it so it doesn't force the list down to one entry.
        const searchDescription = descriptionMatch && descriptionMatch !== equipmentIdMatch ? '' : description;
        const searchEquipmentId = equipmentIdMatch && equipmentIdMatch !== descriptionMatch ? '' : equipmentId;

        const matchingTools = availableTools.filter(
            (tool) =>
                (searchDescription !== '' &&
                    tool.description.toLowerCase().indexOf(searchDescription.toLowerCase()) > -1) ||
                (searchEquipmentId !== '' &&
                    tool.equipmentId.toLowerCase().indexOf(searchEquipmentId.toLowerCase()) > -1),
        );

        return matchingTools.length > 0 ? matchingTools : availableTools;
    });
